import type {
  StartupProgramCatalog,
  StartupProgramFilter,
  StartupProgramCoverage,
} from '@/containers/search/startup-programs/startup-programs.types';
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithAuth } from '../base-query';
import type {
  CompanyCoverage,
  IdentityExclusionKind,
  PeopleIdentityExclusions,
} from '@/store/features/prospects.feature';

/**
 * Flattened CompanyRecord reflecting what /screener/companydb/search
 * actually returns at the top level (verified via live probe on
 * 2026-04-19). Fields marked OPTIONAL/ACCESS-GATED are not in the
 * default response — they require Crustdata enterprise-tier access
 * or a separate endpoint call and are left on the interface only so
 * we can surface them if we enable those upgrades later.
 */
export interface CompanyRecord {
  phone_number?: string;
  contact_email?: string;
  company_phone?: string | null;
  company_contact_email?: string | null;
  linkedin_industry?: string;
  hq_timezone?: string;
  hq_timezone_source?: 'provider' | 'derived';
  maps_locations?: Array<Record<string, unknown>>;
  open_job_count?: number;
  software_review_rating?: number;
  software_review_count?: number;
  software_reviews?: {
    review_count_mom_pct?: number | null;
    review_count_qoq_pct?: number | null;
    review_count_yoy_pct?: number | null;
  };
  // Identity
  company_id: number;
  /** LinkedIn's own internal company id, which is what the backend
   *  dedups imports on (``Company.linkedin_internal_id``). Distinct from
   *  ``company_id``, which is Crustdata's. Confirmed present on a live
   *  row; typed loosely because Crustdata returns the numeric id as
   *  either a number or its string form depending on the endpoint. */
  linkedin_id?: string | number;
  company_name: string;
  company_website_domain?: string;
  company_website?: string;
  /** Crustdata ships this as ``linkedin_profile_url``. Kept alias
   *  for back-compat with older cached responses. */
  company_linkedin_url?: string;
  linkedin_profile_url?: string;
  /** Crustdata ships this as ``logo_permalink``. Kept alias too. */
  company_logo_url?: string;
  logo_permalink?: string;
  // Location
  hq_location?: string;
  hq_country?: string;
  // Size
  employee_count?: number;
  employee_count_range?: string;
  // Industry / categorization
  linkedin_industries?: string[];
  linkedin_categories?: string[];
  crunchbase_categories?: string[];
  year_founded?: number;
  company_type?: string;
  // Revenue (estimated band is in default response; exact numbers aren't)
  estimated_revenue_lower_bound_usd?: number;
  estimated_revenue_higher_bound_usd?: number;
  // Description
  linkedin_company_description?: string;
  description?: string; // alias
  // Follower metrics — in default response under follower_metrics
  follower_metrics?: {
    latest_count?: number;
    growth_1m?: number;
    growth_3m?: number;
    growth_6m?: number;
    growth_12m?: number;
    growth_1m_percent?: number;
    growth_3m_percent?: number;
    growth_6m_percent?: number;
    growth_12m_percent?: number;
  };
  follower_count?: number; // alias, back-compat
  // ACCESS-GATED: require Crustdata upgrade. Kept optional so future
  // enablement doesn't require a type change.
  crunchbase_total_investment_usd?: number;
  last_funding_round_type?: string;
  last_funding_date?: string;
  /** Queried by ``buildCompanyFilters`` (lastFundingRoundAmountMin/Max,
   *  ipoDateAfter) but never declared here, so the add-to-list projection
   *  had no typed way to carry them forward. */
  last_funding_round_investment_usd?: number;
  ipo_date?: string;
  /** Employee growth + exact headcount live nested under
   *  ``employee_metrics`` on the default Crustdata response — same
   *  shape as ``follower_metrics``. The legacy top-level
   *  ``employee_growth_*_percent`` keys never materialized in practice;
   *  every accessor that reads them should fall through to the nested
   *  values here. */
  employee_metrics?: {
    latest_count?: number;
    growth_1m?: number;
    growth_3m?: number;
    growth_6m?: number;
    growth_12m?: number;
    growth_1m_percent?: number;
    growth_3m_percent?: number;
    growth_6m_percent?: number;
    growth_12m_percent?: number;
  };
  /** Legacy top-level aliases — preserved as optional so cache blobs
   *  that stored them keep typechecking. New responses fill
   *  ``employee_metrics.*`` instead. */
  employee_growth_1m_percent?: number;
  employee_growth_3m_percent?: number;
  employee_growth_6m_percent?: number;
  employee_growth_12m_percent?: number;
  /** Stock listing exchange (e.g. "NASDAQ", "NYSE", "PRIVATE"). */
  markets?: string | string[];
  /** Public-company ticker. */
  stock_symbols?: string[];
  /** Acquisition status. Crustdata emits ``acquisition_status`` (the
   *  canonical value being ``"acquired"``) — the same key
   *  ``buildCompanyFilters`` has always sent on the ``acquiredOnly``
   *  filter. A live row confirms ``acquisition_status`` is present and
   *  ``acquired`` is absent, so the boolean below never arrives; it and
   *  ``acquisition_date`` stay only as fallbacks for cached blobs. */
  acquisition_status?: string;
  /** @deprecated never emitted by Crustdata — use acquisition_status. */
  acquired?: boolean;
  /** @deprecated never emitted by Crustdata — use acquisition_status. */
  acquisition_date?: string;
  /** Investor names. Crustdata emits ``crunchbase_investors`` — again the
   *  key ``buildCompanyFilters`` already sends. Verified present on a live
   *  row while ``investors`` was absent. */
  crunchbase_investors?: string[];
  /** @deprecated never emitted by Crustdata — use crunchbase_investors. */
  investors?: string[];
  /** ISO-3 of the country with the largest headcount. */
  largest_headcount_country?: string;
  /** Last data refresh timestamp. */
  updated_at?: string;
  /** Twitter handle (no @). */
  twitter_handle?: string;
  specialties?: string[]; // deprecated — use linkedin_categories
  // ── Mapper passthrough ──
  // CompanyDB columns the results grid never renders, declared so the
  // add-to-list projection (``toCompanyImportInput``) has a typed way to
  // forward them to the backend mapper. Crustdata's shapes for these are
  // inconsistent across plans and endpoints, so the non-string ones are
  // typed loosely and passed through verbatim instead of normalized.
  crunchbase_profile_url?: string;
  /** Crunchbase's own UUID for the company. */
  crunchbase_uuid?: string;
  fiscal_year_end?: string;
  /** Date the growth metrics were computed on. */
  growth_calculation_date?: string;
  /** When Crustdata last indexed the company. */
  indexed_at?: string;
  /** Alternate/secondary website domains. */
  domains?: unknown[];
  office_addresses?: unknown[];
  /** Crustdata company ids of competitors. */
  competitor_ids?: unknown[];
  competitor_websites?: unknown[];
  // Workforce-composition maps (role/region/skill → count or percent).
  role_distribution?: Record<string, unknown>;
  role_distribution_percent?: Record<string, unknown>;
  role_growth_6m?: Record<string, unknown>;
  role_growth_yoy?: Record<string, unknown>;
  region_distribution?: Record<string, unknown>;
  skill_distribution?: Record<string, unknown>;
  skill_distribution_percent?: Record<string, unknown>;
  /** Investor names as tracked by Tracxn. */
  tracxn_investors?: unknown[];
  /** Free-form street + city line — finer-grained than ``hq_location``. */
  hq_street_address_and_city?: string;
  /** Structured address components for the HQ. */
  hq_location_address_components?: unknown[];
  /** Full Twitter/X profile URL — distinct from ``twitter_handle``. */
  twitter_profile_url?: string;
}

/** One CRM exclude clause as the company endpoints read it. */
export interface CompanyExclusionClause {
  id: string;
  polarity: 'exclude';
  condition: string;
}

/** Companies to leave out of a Find Companies search, resolved server-side
 *  to one set of domains. */
export interface CompanyExclusions {
  excludeCompanyDomains: string[];
  excludeInCleanlist: 'off' | 'any' | 'list';
  excludeInCleanlistListIds: string[];
  crmFilterSections: {
    salesforce: CompanyExclusionClause[];
    hubspot: CompanyExclusionClause[];
  };
  /** Re-read the CRM sets instead of using the cached ones (throttled
   *  server-side). */
  refreshCrm: boolean;
}

/** What the server did with the excludes. ``sent_to_search`` below
 *  ``excluded_domains`` means only part of the set reached the search, so the
 *  total is an upper bound. */
export interface CompanyExclusionSummary {
  excluded_domains: number;
  sent_to_search: number;
  removed_from_page: number;
  /** With CRM clauses: the CRM records they matched and the domains those
   *  gave. A record with no website has no domain to exclude. */
  crm_records?: number;
  crm_domains?: number;
}

export interface CompanySearchRequest {
  startup_programs?: StartupProgramFilter;
  filters: Record<string, unknown>;
  limit?: number;
  sorts?: Array<{ column: string; order: 'asc' | 'desc' }>;
  /** Forward-only paging cursor. Omitted for block 1; for later blocks
   *  send the previous response's ``next_cursor`` with the SAME filters/
   *  sort to get the next block. */
  cursor?: string;
  /** Company excludes. Paging and the import fetch send the same ones. */
  exclusions?: CompanyExclusions;
}

export interface CompanySearchResponse {
  startup_program_coverage?: StartupProgramCoverage;
  program_warning?: string;
  companies: CompanyRecord[];
  total_count: number | null;
  next_cursor?: string;
  /** Present when ``exclusions`` named at least one exclude. */
  exclusion_summary?: CompanyExclusionSummary;
}

// ─── Import companies to a list ─────────────────────────────────────
//
// /company-search/import kicks off a FindPeopleFromCompaniesWorkflow
// that pulls ``limit_per_company`` contacts for each selected company
// and imports them into the target lead list. Returns immediately
// with a workflow_id; progress streams over the existing
// ``find_people_progress`` WebSocket channel.

/** Growth block Crustdata nests under ``employee_metrics`` /
 *  ``follower_metrics``. Sent nested so the persisted blob keeps
 *  Crustdata's own shape and stays re-mappable without a re-fetch. */
export interface CompanyMetricsInput {
  latest_count?: number;
  growth_1m?: number;
  growth_3m?: number;
  growth_6m?: number;
  growth_12m?: number;
  growth_1m_percent?: number;
  growth_3m_percent?: number;
  growth_6m_percent?: number;
  growth_12m_percent?: number;
}

/**
 * Wire shape of one company on /company-search/import. Mirrors the
 * backend's ``CompanyImportInput`` and uses Crustdata's own field names
 * throughout, so a widened backend can map columns and stash the rest
 * verbatim into ``companies.crustdata_payload``.
 *
 * ``markets`` is the one shape deviation: Crustdata hands it back as
 * either a bare string or a list, and a union on the wire forces every
 * consumer to re-handle both. It is normalized to a list here.
 */
export interface CompanyImportInput {
  phone_number?: string;
  contact_email?: string;
  linkedin_industry?: string;
  hq_timezone?: string;
  hq_timezone_source?: 'provider' | 'derived';
  maps_locations?: Array<Record<string, unknown>>;
  open_job_count?: number;
  software_review_rating?: number;
  software_review_count?: number;
  software_reviews?: {
    review_count_mom_pct?: number | null;
    review_count_qoq_pct?: number | null;
    review_count_yoy_pct?: number | null;
  };
  // ── Identity ──
  company_id?: number;
  /** LinkedIn's internal company id — the key the backend dedups on.
   *  Sent as a string: ``Company.linkedin_internal_id`` is a text column
   *  everywhere it is written, and Pydantic will coerce a numeric string
   *  into an int field but not an int into a str one, so the string form
   *  is the one that validates whichever way the model declares it. */
  linkedin_id?: string;
  company_name: string;
  linkedin_profile_url?: string;
  company_website_domain?: string;
  company_website?: string;
  logo_permalink?: string;
  twitter_handle?: string;
  // ── Location ──
  hq_location?: string;
  hq_country?: string;
  largest_headcount_country?: string;
  // ── Size ──
  employee_count?: number;
  employee_count_range?: string;
  employee_metrics?: CompanyMetricsInput;
  // ── Industry / categorization ──
  linkedin_industries?: string[];
  linkedin_categories?: string[];
  crunchbase_categories?: string[];
  year_founded?: number;
  company_type?: string;
  // ── Revenue ──
  estimated_revenue_lower_bound_usd?: number;
  estimated_revenue_higher_bound_usd?: number;
  // ── Description ──
  linkedin_company_description?: string;
  // ── Audience ──
  follower_count?: number;
  follower_metrics?: CompanyMetricsInput;
  // Funding
  crunchbase_total_investment_usd?: number;
  last_funding_round_type?: string;
  last_funding_date?: string;
  last_funding_round_investment_usd?: number;
  ipo_date?: string;
  // ── Public listing ──
  markets?: string[];
  stock_symbols?: string[];
  // ── Status / provenance ──
  acquisition_status?: string;
  crunchbase_investors?: string[];
  updated_at?: string;
  // ── Mapper passthrough ──
  // Carried over from the search row unreshaped: the backend mapper
  // (``transform_companydb_to_company_fields``) maps each of these to a
  // real Company column, and the endpoint model is ``extra="allow"`` so
  // whatever it doesn't map still lands in the retained payload. The
  // backend prunes the heavy families before storage, so these cost
  // request bytes, not table bytes.
  crunchbase_profile_url?: string;
  crunchbase_uuid?: string;
  fiscal_year_end?: string;
  growth_calculation_date?: string;
  indexed_at?: string;
  domains?: unknown[];
  office_addresses?: unknown[];
  competitor_ids?: unknown[];
  competitor_websites?: unknown[];
  role_distribution?: Record<string, unknown>;
  role_distribution_percent?: Record<string, unknown>;
  role_growth_6m?: Record<string, unknown>;
  role_growth_yoy?: Record<string, unknown>;
  region_distribution?: Record<string, unknown>;
  skill_distribution?: Record<string, unknown>;
  skill_distribution_percent?: Record<string, unknown>;
  tracxn_investors?: unknown[];
  hq_street_address_and_city?: string;
  hq_location_address_components?: unknown[];
  twitter_profile_url?: string;
}

export interface ImportCompaniesRequest {
  lead_list_id: string;
  companies: CompanyImportInput[];
}

/** What became of one requested company, in request order (additive).
 *  ``company_id`` is echoed as the request sent it. */
export interface CompanyImportOutcome {
  index: number;
  company_id: number | null;
  name: string;
  list_company_id: string | null;
  outcome: 'added' | 'already_in_list' | 'in_batch_duplicate' | 'error';
  error: string | null;
}

export interface ImportCompaniesResponse {
  success: boolean;
  imported_count: number;
  skipped_count: number;
  credits_charged: number;
  lead_ids: string[];
  errors: string[];
  /** One per requested company; absent from an API that predates it. */
  outcomes?: CompanyImportOutcome[];
}

// ─── Composite (people-at-companies) ─────────────────────────────────
//
// Resolves a CompanyDB filter into a company_linkedin_profile_url
// list (server-side, cached) → runs PersonDB scoped to that set.
// Used wherever the user wants to filter people by firmographic
// criteria PersonDB doesn't expose natively (revenue, funding, growth).

export interface CompositePeopleSearchRequest {
  startup_programs?: StartupProgramFilter;
  company_filter?: Record<string, unknown>;
  company_linkedin_urls?: string[];
  person_filter: Record<string, unknown>;
  limit?: number;
  limit_per_company?: number;
  cursor?: string;
  sorts?: Array<{ column: string; order: 'asc' | 'desc' }>;
  post_processing?: { exclude_profiles?: string[]; exclude_names?: string[] };
  identity_exclusions?: PeopleIdentityExclusions;
}

export interface CompositePeopleSearchResponse {
  startup_program_coverage?: StartupProgramCoverage;
  program_warning?: string;
  // Mirrors PeopleSearchResponse (results, total_count, next_cursor) +
  // composite diagnostics.
  results: Array<Record<string, unknown>>;
  total_count?: number | null;
  page?: number;
  page_size?: number;
  next_cursor?: string;
  applied_identity_exclusion_token?: string;
  /** The identity exclusion kinds the server applied (the async job and its
   *  poll carry them beside the token). */
  applied_identity_exclusions?: IdentityExclusionKind[] | null;
  instantly_unchecked?: number | null;
  resolved_company_count: number;
  composite_warning?: string;
  cache_hit: boolean;
  company_coverage?: CompanyCoverage | null;
}

// Async companies-find: the composite "people at these companies" search.
// Start a job, then poll `getCompaniesFindStatus` until `status` !==
// 'processing'. One job == one cursor block, so the status payload mirrors
// CompositePeopleSearchResponse.
export interface CompaniesFindStartResponse {
  task_id: string;
  status: string;
  poll_url: string;
}

export type CompaniesFindStatusResponse = CompositePeopleSearchResponse & {
  /** Sanitized startup-program job failure, when present. */
  error_message?: string;
  /** A failed job's error detail + HTTP status (e.g. the search admission
   *  402/403), which the inline call would have rejected with. */
  error?: string | Record<string, unknown>;
  status_code?: number;
  task_id: string;
  status: string;
};

/** One logo.dev brand: a picked row becomes an exact domain filter. */
export type BrandSuggestion = { name: string; domain: string };

export type SearchCountKind = 'people' | 'people-at-companies' | 'companies';

const SEARCH_COUNT_URLS: Record<SearchCountKind, string> = {
  people: '/people/find/count',
  'people-at-companies': '/people/find-at-companies/inline/count',
  companies: '/company-search/count',
};

export const companySearchApi = createApi({
  reducerPath: 'companySearchApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['CompanySearch'],
  endpoints: (builder) => ({
    getStartupPrograms: builder.query<StartupProgramCatalog, void>({
      query: () => '/company-search/startup-programs',
    }),
    companySearch: builder.mutation<
      CompanySearchResponse,
      CompanySearchRequest
    >({
      query: (payload) => ({
        url: '/company-search/search',
        method: 'POST',
        body: payload,
      }),
    }),
    // Import-only "fetch more" — byte-for-byte mirror of companySearch.
    companyImportFetch: builder.mutation<
      CompanySearchResponse,
      CompanySearchRequest
    >({
      query: (payload) => ({
        url: '/company-search/import-fetch',
        method: 'POST',
        body: payload,
      }),
    }),
    // The Finder's Show button count: the same body Show would send, posted
    // to that route's /count, which answers { total } (a number, or null
    // where no cheap count exists). Never debits a search allowance.
    searchResultCount: builder.mutation<
      { total: number | null },
      { kind: SearchCountKind; body: Record<string, unknown> }
    >({
      query: ({ kind, body }) => ({
        url: SEARCH_COUNT_URLS[kind],
        method: 'POST',
        body,
      }),
    }),
    companyAutocomplete: builder.query<
      string[],
      { field: string; query: string; limit?: number }
    >({
      query: ({ field, query, limit = 20 }) => ({
        url: '/company-search/autocomplete',
        method: 'POST',
        body: { field, query, limit },
      }),
    }),
    // Company-name typeahead (logo.dev Search Brands, proxied server-side).
    brandSuggest: builder.query<BrandSuggestion[], string>({
      query: (q) => ({ url: '/company-search/brand-suggest', params: { q } }),
      keepUnusedDataFor: 300,
    }),
    importCompanies: builder.mutation<
      ImportCompaniesResponse,
      ImportCompaniesRequest
    >({
      query: (payload) => ({
        url: '/company-search/import',
        method: 'POST',
        body: payload,
      }),
    }),
    // Import-only "fetch more" for the composite search.
    peopleAtCompaniesImportFetch: builder.mutation<
      CompositePeopleSearchResponse,
      CompositePeopleSearchRequest
    >({
      query: (payload) => ({
        url: '/people/find-at-companies/inline/preview',
        method: 'POST',
        body: payload,
      }),
    }),
    // Async composite search: start a job (non-blocking), poll for the block.
    companiesFindStart: builder.mutation<
      CompaniesFindStartResponse,
      CompositePeopleSearchRequest
    >({
      query: (payload) => ({
        url: '/people/find-at-companies',
        method: 'POST',
        body: payload,
      }),
    }),
    getCompaniesFindStatus: builder.query<CompaniesFindStatusResponse, string>({
      query: (taskId) => ({
        url: `/people/find-at-companies/status/${taskId}`,
        method: 'GET',
      }),
      keepUnusedDataFor: 0,
    }),
  }),
});

export const {
  useGetStartupProgramsQuery,
  useCompanySearchMutation,
  useCompanyImportFetchMutation,
  useCompanyAutocompleteQuery,
  useLazyCompanyAutocompleteQuery,
  useBrandSuggestQuery,
  useImportCompaniesMutation,
  usePeopleAtCompaniesImportFetchMutation,
  useCompaniesFindStartMutation,
  useLazyGetCompaniesFindStatusQuery,
  useSearchResultCountMutation,
} = companySearchApi;
